import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';
import formatTimestamp from '../../../i18n/formatTimestamp';
import { withFlags } from '../../../flags/Flags';

import {
  DataTable,
  DataTableColumn,
  DataTableCell,
  Dropdown,
  Icon,
  Tooltip
} from '@salesforce/design-system-react';

import './AssetClassList.scss';

const CustomDataTableCell = ({ children, property, ...props }) => {
  const isParent = props.item.child_ids?.length;
  const isChild = !!props.item.parent_class_id;
  const childCellClass = isChild ? 'child-cell' : '';

  switch (property) {
    case 'expand':
      const expanded = props.expandedRows.includes(props.item.uuid);
      const icon = expanded ? 'chevrondown' : 'chevronright';
      if (isParent) {
        return (
          <td
            onClick={() => props.onAction('toggleExpand', props.item)}
            className="chevron-cell is-parent"
          >
            <Icon category="utility" name={icon} size="xx-small" />
          </td>
        );
      } else {
        return <td className={`chevron-cell ${childCellClass}`} />;
      }
    case 'actions':
      const isSaveAllowed = !props.flags?.isReadonly;
      const has_selectable = props.item?.has_selectable;
      // The tooltip needs undefined for isOpen value, in order to open on hover
      const open = has_selectable ? undefined : false;
      let disabled = has_selectable || !isSaveAllowed;
      const actions = [
        {
          className: 'edit-asset-class',
          label: props.formatMessage({
            id: 'components.ecs.assetBrowser.menu.edit'
          }),
          value: 'edit'
        }
      ];

      if (props.item.blockCount + props.item.assetCount === 0) {
        actions.push({
          className: 'archive-asset-class',
          disabled: !isSaveAllowed,
          label: props.formatMessage({
            id: 'components.ecs.assetBrowser.menu.delete'
          }),
          value: 'delete'
        });
      }

      if (!isChild) {
        const archiveType = props.item.archived ? 'unarchive' : 'archive';
        disabled = !isSaveAllowed;
        actions.push({
          disabled,
          className: 'archive-asset-class-disabled',
          label: props.formatMessage({
            id: `components.ecs.assetBrowser.menu.${archiveType}`
          }),
          value: archiveType
        });
      }

      const dropDown = (
        <Dropdown
          assistiveText={{ icon: 'Asset Options' }}
          buttonVariant="icon"
          iconCategory="utility"
          iconName="down"
          iconVariant="border-filled"
          align="right"
          className="actions-dropdown"
          width="xx-small"
          options={actions}
          onSelect={({ value }) => props.onAction(value, props.item, disabled)}
          tooltipMenuItem={
            <Tooltip
              id="drop-down-tooltip"
              align="left"
              position="overflowBoundaryElement"
              isOpen={open}
            />
          }
        />
      );
      return (
        <td className={`asset-class-cell actions-cell ${childCellClass}`}>
          {dropDown}
        </td>
      );
    case 'description':
      const desc = props.item.description || '';
      const isTruncated = desc.length > 128;
      const truncated = desc.substring(0, 125).trimEnd() + '...';
      return (
        <td className={`asset-class-cell wrap-text ${childCellClass}`}>
          {isTruncated ? truncated : children}
        </td>
      );
    default: {
      return (
        <td className={`asset-class-cell wrap-text ${childCellClass}`}>
          {children}
        </td>
      );
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object
};

class AssetClassList extends Component {
  headerCell(property, tooltip, formatMessage) {
    const text = formatMessage({ id: `components.ecs.classes.${property}` });

    if (tooltip) {
      const tooltipText = formatMessage({ id: tooltip });
      return (
        <div className="with-tooltip">
          {text}
          <Tooltip
            triggerStyle={{ lineHeight: null }}
            align="right"
            content={tooltipText}
            position="overflowBoundaryElement"
          />
        </div>
      );
    } else {
      return text;
    }
  }

  columns() {
    const expand = {};
    if (this.props.showExpandColumn) {
      expand.expand = { sortable: false, width: '20px' };
    }

    const nameColumn = {
      name: { sortable: false, width: '300px' }
    };
    const weatherColumn = this.props.showWeatherColumn
      ? {
          weatherColumn: {
            sortable: false,
            width: '200px',
            tooltip: 'components.ecs.classes.weather.columnTooltip'
          }
        }
      : {};

    const standardCols = {
      description: { sortable: false },
      assetCount: { sortable: false, width: '100px' },
      blockCount: { sortable: false, width: '100px' },
      updatedAt: { sortable: false },
      actions: { sortable: false }
    };
    return { ...expand, ...nameColumn, ...weatherColumn, ...standardCols };
  }

  tableData(items) {
    const { formatMessage } = this.props.intl;
    const Y = formatMessage({ id: 'components.ecs.attributes.Y' });
    const N = formatMessage({ id: 'components.ecs.attributes.N' });
    return items.map(item => ({
      ...item,
      id: item.uuid,
      assetCount: item.count,
      blockCount: item.blocks,
      weatherColumn: item.weather_aware ? Y : N,
      updatedAt: formatTimestamp(this.props.intl)(item.updated_at)
    }));
  }

  renderTable(items) {
    const { formatMessage } = this.props.intl;
    const columns = this.columns();

    return withFlags(flags => (
      <React.Fragment>
        <DataTable
          items={items}
          className="asset-class-list-table"
          fixedLayout
          fixedHeader={!!this.props.unfilteredTypes?.length}
          noRowHover
        >
          {Object.entries(columns).map((column, index) => {
            const sortable = column[1].sortable;
            const width = column[1].width;
            const tooltip = column[1].tooltip;
            const property = column[0];
            return (
              <DataTableColumn key={index} sortable={sortable}>
                <CustomDataTableCell
                  property={property}
                  formatMessage={formatMessage}
                  label={this.headerCell(property, tooltip, formatMessage)}
                  width={width}
                  expandedRows={this.props.expandedRows || []}
                  onAction={this.props.onListAction}
                  flags={flags}
                />
              </DataTableColumn>
            );
          })}
        </DataTable>
      </React.Fragment>
    ));
  }

  render() {
    const { types } = this.props;

    return this.renderTable(this.tableData(types));
  }
}

AssetClassList.propTypes = {
  types: PropTypes.array.isRequired,
  onListAction: PropTypes.func,
  showExpandColumn: PropTypes.bool,
  showWeatherColumn: PropTypes.bool,
  expandedRows: PropTypes.array,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(AssetClassList);
